/**
 * Projecao da comissao do mes corrente no ritmo atual.
 *
 * Decisoes nao obvias:
 *  1. O mes e o do CALENDARIO, independente da janela do cabecalho; o titulo
 *     diz o nome do mes.
 *  2. O realizado ate hoje e a soma das comissoes CONGELADAS (servidor). So a
 *     parte que ainda vai acontecer usa o percentual VIGENTE de cada empresa
 *     (`useCompany`): se a administracao mudou o percentual hoje, a projecao
 *     muda, o realizado nao. A tela diz quais empresas mudaram.
 *  3. Projecao linear inteira, por empresa: recuperado x dias do mes / dias
 *     decorridos. O rodape avisa que nao considera sazonalidade.
 *  4. "Fatura de setembro": o valor que sairia em 1º de outubro SE o modelo
 *     for contrato mensal -- o modelo ainda e decisao do dono, e o texto diz
 *     isso em vez de afirmar uma cobranca.
 */

import { Footnote, ProgressBar, SectionCard } from '../../../components/ui';
import { formatBRLFromCents, formatCount, formatPercentFromBps } from '../../../lib/format';
import type { CompanySummary } from '../../../types';
import { aplicarBps, projetar } from '../comparar';
import type { CompanyCommissionSummary, MonthToDate } from '../types';
import { nomeDoMes, plural } from '../vocabulario';
import { Delta } from './Delta';

/** Quantas empresas com percentual alterado cabem na frase; o resto vira "e mais N" (escala). */
const MAXIMO_DE_ALTERADAS = 3;

interface Projecao {
  readonly realizadoCents: number;
  readonly realizadoRecuperadoCents: number;
  readonly vendas: number;
  readonly comissaoCents: number;
  readonly recuperadoCents: number;
  readonly alteradas: readonly { readonly nome: string; readonly antes: number; readonly agora: number }[];
}

function projetarMes(mes: MonthToDate, rows: readonly CompanyCommissionSummary[], empresas: readonly CompanySummary[]): Projecao | null {
  let realizadoCents = 0;
  let realizadoRecuperadoCents = 0;
  let vendas = 0;
  let comissaoCents = 0;
  let recuperadoCents = 0;
  const alteradas: { nome: string; antes: number; agora: number }[] = [];
  for (const c of mes.byCompany) {
    const recuperadoProjetado = projetar(c.recoveredAmountCents, mes.daysElapsed, mes.daysInMonth);
    if (recuperadoProjetado === null) {
      return null;
    }
    const row = rows.find((r) => r.companyId === c.companyId);
    const feeServidor = row?.feeBps ?? 0;
    const feeVigente = empresas.find((e) => e.id === c.companyId)?.feeBps ?? feeServidor;
    if (row !== undefined && feeVigente !== feeServidor && c.conversionCount > 0) {
      alteradas.push({ nome: row.companyName, antes: feeServidor, agora: feeVigente });
    }
    realizadoCents += c.commissionCents;
    realizadoRecuperadoCents += c.recoveredAmountCents;
    vendas += c.conversionCount;
    recuperadoCents += recuperadoProjetado;
    comissaoCents += c.commissionCents + aplicarBps(Math.max(0, recuperadoProjetado - c.recoveredAmountCents), feeVigente);
  }
  return { realizadoCents, realizadoRecuperadoCents, vendas, comissaoCents, recuperadoCents, alteradas };
}

function proximoMesDia1(monthKey: string): string {
  const [ano, mes] = monthKey.split('-').map(Number);
  const data = new Date(Date.UTC(ano ?? 1970, mes ?? 1, 1));
  return `01/${String(data.getUTCMonth() + 1).padStart(2, '0')}`;
}

export function ProjecaoDoMes({
  mes,
  rows,
  empresas,
  className = '',
}: {
  readonly mes: MonthToDate;
  readonly rows: readonly CompanyCommissionSummary[];
  /** Cadastro vigente (useCompany), para o percentual dos dias que faltam. */
  readonly empresas: readonly CompanySummary[];
  readonly className?: string;
}): JSX.Element {
  const nome = nomeDoMes(mes.monthKey);
  const p = projetarMes(mes, rows, empresas);

  return (
    <SectionCard
      id="projecao-do-mes"
      title={`Projeção de ${nome}`}
      icon="calendario"
      description="Comissão no ritmo dos dias já decorridos, independente da janela do cabeçalho."
      className={className}
    >
      <div className="flex h-full flex-col justify-between gap-4">
        {p === null ? (
          <p className="text-sm text-ink-subtle">O mês acabou de começar: ainda não há ritmo para projetar.</p>
        ) : (
          <div className="flex flex-col gap-4">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">Fecha {nome} em, no ritmo atual</p>
              <p className="mt-0.5 text-3xl font-semibold tracking-tight tabular-nums text-ink">
                <span aria-hidden="true">≈ </span>
                <span className="sr-only">aproximadamente </span>
                {formatBRLFromCents(p.comissaoCents)}
              </p>
              <p className="mt-1 text-sm text-ink-muted">
                de comissão sobre ≈ {formatBRLFromCents(p.recuperadoCents)} recuperados para as empresas
              </p>
              <div className="mt-1 flex flex-wrap items-center gap-x-2 text-xs text-ink-subtle">
                <Delta atual={p.comissaoCents} anterior={mes.previousMonthCommissionCents} tipo="dinheiro" />
                <span>sobre o mês anterior fechado ({formatBRLFromCents(mes.previousMonthCommissionCents)})</span>
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-ink-muted">
                <span>
                  Realizado até hoje: <strong className="font-semibold tabular-nums text-ink">{formatBRLFromCents(p.realizadoCents)}</strong> ·{' '}
                  {formatCount(p.vendas)} {plural(p.vendas, 'venda', 'vendas')}
                </span>
                <span className="tabular-nums">
                  {formatCount(mes.daysElapsed)} de {formatCount(mes.daysInMonth)} dias
                </span>
              </div>
              <ProgressBar value={mes.daysElapsed} max={mes.daysInMonth} tone="neutral" label="Dias do mês decorridos" />
            </div>

            <div className="rounded-xl border border-line bg-elevated px-3 py-2.5 text-xs text-ink-muted">
              Se o modelo for <strong className="font-semibold text-ink">contrato mensal</strong>, a fatura de {nome} sai em {proximoMesDia1(mes.monthKey)} com ≈{' '}
              <strong className="font-semibold tabular-nums text-ink">{formatBRLFromCents(p.comissaoCents)}</strong>. O modelo de cobrança ainda é decisão do dono.
            </div>

            {p.alteradas.length === 0 ? null : (
              <p className="text-xs text-ink-muted">
                Os dias que faltam usam o percentual vigente:{' '}
                {p.alteradas.slice(0, MAXIMO_DE_ALTERADAS).map((a, i) => (
                  <span key={a.nome}>
                    {i > 0 ? '; ' : ''}
                    <strong className="font-semibold text-ink">{a.nome}</strong> {formatPercentFromBps(a.agora)} (era {formatPercentFromBps(a.antes)})
                  </span>
                ))}
                {p.alteradas.length > MAXIMO_DE_ALTERADAS
                  ? ` e mais ${formatCount(p.alteradas.length - MAXIMO_DE_ALTERADAS)} ${plural(p.alteradas.length - MAXIMO_DE_ALTERADAS, 'empresa', 'empresas')} (veja o selo “alterado” no resumo por empresa)`
                  : null}
                . O realizado continua congelado.
              </p>
            )}
          </div>
        )}
        <Footnote>Projeção linear por empresa: realizado dividido pelos dias decorridos, vezes os dias do mês. Não considera sazonalidade nem estornos futuros.</Footnote>
      </div>
    </SectionCard>
  );
}
